import { useCallback, useEffect, useRef, useState } from "react";
import Footer from "../../layout/Footer";
import Navbar from "../../layout/Navbar";
import Slider from "react-slick";
import axios from "axios";
import { NextArrow, PrevArrow } from "../../components/sliderArrow";
import styles from "./buddhism-lesson.module.sass";
import { useLocation } from "react-router-dom";
import { renderMedia, isVideoFile, isYouTubeUrl } from "../../utils/mediaUtils";
import CarouselList from "../../components/CarouselList";

const BuddhismLessonComponent = function BuddhismLesson() {
  const [nav1, setNav1] = useState();
  const [nav2, setNav2] = useState();
  // const [articleContent, setArticleContent] = useState({});
  const [sliderContent, setSliderContent] = useState([]);
  const [albumSliderContent, setAlbumSliderContent] = useState([]);
  const [albumContent, setAlbumContent] = useState();
  const [currentAlbumIndex, setCurrentAlbumIndex] = useState(0);
  const [lessonContent, setLessonContent] = useState({});
  const videoRef = useRef(null);
  const triggerRef = useRef(null);
  const [playedOnce, setPlayedOnce] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [currentSliderIndex, setCurrentSliderIndex] = useState(0);
  const mainSliderContainerRef = useRef(null);
  const lightboxContainerRef = useRef(null);
  let location = useLocation();

  // 當相簿索引改變時自動載入相簿內容
  const handleAlbumChange = (index) => {
    if (albumSliderContent && albumSliderContent[index]) {
      const albumId = albumSliderContent[index].id;
      // 模擬點擊事件來觸發 handleAlbumClick
      const fakeEvent = {
        currentTarget: {
          getAttribute: () => albumId
        }
      };
      handleAlbumClick(fakeEvent);
      setCurrentAlbumIndex(index);
    }
  };

  // 當相簿清單載入時，設定預設選中的相簿索引（後端已載入第一個相簿內容）
  useEffect(() => {
    if (albumSliderContent && albumSliderContent.length > 0 && currentAlbumIndex === 0) {
      // 不需要再次載入，後端已經載入了第一個相簿的內容
      setCurrentAlbumIndex(0);
    }
  }, [albumSliderContent]);

  const handleAlbumClick = useCallback(
    (e) => {
      let albumID = e.currentTarget.getAttribute("data-id");
      axios
        .post("/api/website/getAlbumContent", { albumId: albumID * 1 })
        .then((response) => {
          setSliderContent(response.data.data.slider);
        })
        .catch((error) => {
          console.error("Error fetching article content:", error);
        });
    },
    [setSliderContent]
  );

  const openLightbox = useCallback(() => {
    setCurrentImageIndex(currentSliderIndex);
    setLightboxOpen(true);
    document.body.style.overflow = 'hidden';
  }, [currentSliderIndex]);

  const openLightboxFromThumbnail = useCallback((index) => {
    // 同步主要 slider 的位置
    if (nav1) {
      nav1.slickGoTo(index);
    }
    setCurrentImageIndex(index);
    setLightboxOpen(true);
    document.body.style.overflow = 'hidden';
  }, [nav1]);

  const closeLightbox = useCallback(() => {
    // 關閉燈箱時，暫停燈箱中的影片（YouTube iframe 會自動處理）
    if (lightboxContainerRef.current) {
      const vids = lightboxContainerRef.current.querySelectorAll('video');
      vids.forEach((vid) => {
        if (typeof vid.pause === 'function') vid.pause();
      });
    }
    setLightboxOpen(false);
    document.body.style.overflow = 'unset';
  }, []);

  const nextImage = useCallback(() => {
    const newIndex = currentImageIndex === sliderContent.length - 1 ? 0 : currentImageIndex + 1;
    setCurrentImageIndex(newIndex);
    setCurrentSliderIndex(newIndex);
    // 同步主要 slider 的位置
    if (nav1) {
      nav1.slickGoTo(newIndex);
    }
  }, [currentImageIndex, sliderContent.length, nav1]);

  const prevImage = useCallback(() => {
    const newIndex = currentImageIndex === 0 ? sliderContent.length - 1 : currentImageIndex - 1;
    setCurrentImageIndex(newIndex);
    setCurrentSliderIndex(newIndex);
    // 同步主要 slider 的位置
    if (nav1) {
      nav1.slickGoTo(newIndex);
    }
  }, [currentImageIndex, sliderContent.length, nav1]);

  const handleKeyDown = useCallback((e) => {
    if (!lightboxOpen) return;
    if (e.key === 'Escape') closeLightbox();
    if (e.key === 'ArrowRight') nextImage();
    if (e.key === 'ArrowLeft') prevImage();
  }, [lightboxOpen, closeLightbox, nextImage, prevImage]);

  useEffect(() => {
    let searchParams = new URLSearchParams(location.search);
    let id = searchParams.get("id");

    axios
      .post("/api/website/getBuddhismLesson", { id: id * 1 })
      .then((response) => {
        setLessonContent(response.data.data.result);
        if (response.data.data.album) {
          // 去除重複並依 order 升冪排序
          const uniqueAlbums = response.data.data.album
            .filter(album => album && album.id)
            .filter((album, index, self) => index === self.findIndex(a => a.id === album.id))
            .sort((a, b) => (Number(a?.order) || 0) - (Number(b?.order) || 0));
          setAlbumSliderContent(uniqueAlbums);
        }
        // 後端會回傳第一個相簿的照片在 slider
        if (response.data.data.slider) {
          setSliderContent(response.data.data.slider);
        }
      })
      .catch((error) => {
        console.error("Error fetching article content:", error);
      });

    const currentTrigger = triggerRef.current;
    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting && videoRef.current && !playedOnce) {
          videoRef.current.play();
          setPlayedOnce(true);
        }
      },
      {
        rootMargin: "0px",
        threshold: 0.1,
      }
    );

    if (currentTrigger) {
      observer.observe(currentTrigger);
    }

    return () => {
      if (currentTrigger) {
        observer.unobserve(currentTrigger);
      }
    };
  }, [playedOnce, location.search]);

  // 主輪播切換時：先暫停所有影片，再嘗試播放當前可見的影片
  useEffect(() => {
    if (!mainSliderContainerRef.current) return;
    const container = mainSliderContainerRef.current;
    const videos = container.querySelectorAll('video');
    videos.forEach((vid) => {
      if (typeof vid.pause === 'function') {
        vid.pause();
      }
    });
    // 播放目前 slick-current 內的影片（若有）
    const currentVideo = container.querySelector('.slick-current video');
    if (currentVideo && typeof currentVideo.play === 'function') {
      // 為了避免自動播放被瀏覽器攔截，確保靜音
      currentVideo.muted = true;
      const p = currentVideo.play();
      if (p && typeof p.catch === 'function') {
        // 忽略自動播放被攔截的例外
        p.catch(() => undefined);
      }
    }
    // 暫停所有 YouTube（若有）
    const iframes = container.querySelectorAll('iframe[src*="youtube.com/embed"]');
    iframes.forEach((ifr) => {
      try {
        ifr.contentWindow?.postMessage(JSON.stringify({ event: "command", func: "pauseVideo", args: [] }), "*");
      } catch (e) { void e; }
    });
    // 播放目前 slick-current 的 YouTube 影片（若有）
    const currentIframe = container.querySelector('.slick-current iframe[src*="youtube.com/embed"]');
    if (currentIframe) {
      try {
        currentIframe.contentWindow?.postMessage(JSON.stringify({ event: "command", func: "mute", args: [] }), "*");
        currentIframe.contentWindow?.postMessage(JSON.stringify({ event: "command", func: "playVideo", args: [] }), "*");
      } catch (e) { void e; }
    }
  }, [currentSliderIndex]);

  // 監聽 YouTube 播放狀態，結束後自動切到下一張（主輪播）
  useEffect(() => {
    const onMessage = (event) => {
      let data = null;
      try {
        data = typeof event.data === "string" ? JSON.parse(event.data) : event.data;
      } catch { data = null; }
      if (!data || data.event !== "onStateChange" || data.info !== 0) return; // 0: ended
      const container = mainSliderContainerRef.current;
      if (!container) return;
      const iframes = container.querySelectorAll('iframe[src*="youtube.com/embed"]');
      let isFromHere = false;
      iframes.forEach((ifr) => {
        if (isFromHere) return;
        try {
          if (ifr.contentWindow === event.source) isFromHere = true;
        } catch (e) { void e; }
      });
      if (!isFromHere) return;
      if (nav1 && typeof nav1.slickNext === 'function') {
        nav1.slickNext();
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [nav1]);

  // 移除自動捲動補償：避免任何非使用者觸發的捲動

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  // 燈箱內媒體切換：自動播放當前影片並暫停其他
  useEffect(() => {
    if (!lightboxOpen) return;
    const container = lightboxContainerRef.current;
    if (!container) return;
    const videos = container.querySelectorAll('video');
    videos.forEach((vid) => {
      if (typeof vid.pause === 'function') vid.pause();
    });
    // 只處理非 YouTube 的影片（YouTube iframe 不需要手動控制播放）
    if (isVideoFile(sliderContent[currentImageIndex]?.image) && !isYouTubeUrl(sliderContent[currentImageIndex]?.image)) {
      const currentVideo = container.querySelector('video');
      if (currentVideo) {
        // 為了提升自動播放成功率，預設靜音
        currentVideo.muted = true;
        const p = currentVideo.play();
        if (p && typeof p.catch === 'function') {
          p.catch(() => undefined);
        }
      }
    }
  }, [lightboxOpen, currentImageIndex, sliderContent]);

  const renderAlbumContent = (
    <div className="[&>*:not(:last-child)]:mb-16 [&>*:not(:last-child)]:mobile:mb-8">
      {albumSliderContent.length > 1 ? (
        <section className="page-album-list pt-[44px] mobile:pt-6">
          <div className="text-center">
            <h2 className="text-[#3D3D3D] font-normal text-5xl mb-4 table:text-4xl table:mb-3 mobile:text-3xl mobile:mb-2">
              相簿清單
            </h2>
          </div>
          <div className="max-w-[800px] mx-auto table:max-w-[600px] mobile:max-w-[300px]">
            <CarouselList
              items={albumSliderContent || []}
              currentIndex={currentAlbumIndex}
              onSelect={(idx) => handleAlbumChange(idx)}
              aspectClassName="aspect-[3/2]"
              followSelection={false}
            />
          </div>
        </section>
      ) : null}
      {sliderContent && sliderContent.length !== 0 && (
        <div>
          <div className="text-center mb-[44px] table:mb-14 mobile:mb-2">
            <h2 className="text-[#3D3D3D] font-normal text-5xl mb-4 table:text-4xl table:mb-3 mobile:text-3xl mobile:mb-2">
              活動照片
            </h2>
            <h3 className="text-[#696969] font-normal text-xl table:text-lg mobile:text-base">
              Activity photos
            </h3>
          </div>
          <div ref={mainSliderContainerRef} className="page2-slider w-[50%] shadow-xl mx-auto mb-12 table:w-[75%] mobile:w-[75%]">
            <Slider
              asNavFor={nav2}
              focusOnSelect={false}
              ref={(slider) => setNav1(slider)}
              className="m-auto"
              fade={true}
              accessibility={false}
              prevArrow={<PrevArrow customClassName={styles.prevArrow} />}
              nextArrow={<NextArrow customClassName={styles.nextArrow} />}
              beforeChange={(oldIndex, newIndex) => setCurrentSliderIndex(newIndex)}
            >
              {sliderContent.map((item, index) => (
                <div key={`SliderMain${index}`}>
                  <div 
                    className={`w-full h-[416px] relative overflow-hidden flex justify-center items-center ${currentSliderIndex === index ? 'cursor-pointer pointer-events-auto z-10' : 'pointer-events-none z-0'}`}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      const current = sliderContent[currentSliderIndex];
                      if (!current) return;
                      const isVideo = isVideoFile(current.image);
                      const isYouTube = isYouTubeUrl(current.image);
                      if (!isVideo && !isYouTube) {
                        openLightboxFromThumbnail(currentSliderIndex);
                        return;
                      }
                      if (isVideo) {
                        const videoEl = e.currentTarget.querySelector('video');
                        if (videoEl) {
                          if (videoEl.paused) {
                            videoEl.muted = false;
                            const p = videoEl.play();
                            if (p && typeof p.catch === 'function') p.catch(() => undefined);
                          } else {
                            videoEl.pause();
                          }
                        }
                      }
                      // YouTube 影片點擊時打開 lightbox
                      if (isYouTube) {
                        openLightboxFromThumbnail(currentSliderIndex);
                      }
                    }}
                  >
                    {isVideoFile(item.image)
                      ? (
                        <video
                          src={item.image}
                          className="absolute top-0 left-0 bottom-0 right-0 w-auto h-full m-auto"
                          controls
                          playsInline
                          preload="metadata"
                        >
                          您的瀏覽器不支援影片播放。
                        </video>
                      )
                      : (
                        renderMedia(
                          item.image,
                          "",
                          "absolute top-0 left-0 bottom-0 right-0 w-auto h-full m-auto",
                          {},
                          false,
                          currentSliderIndex === index,
                          null,
                          false
                        )
                      )
                    }
                  </div>
                  <h2 className="text-[#3D3D3D] font-semibold text-3xl table:text-2xl mobile:text-xl">
                    {item.title ? item.title : ""}
                  </h2>
                </div>
              ))}
            </Slider>
          </div>
          <Slider
            asNavFor={nav1}
            ref={(slider) => setNav2(slider)}
            slidesToShow={5}
            infinite={sliderContent.length > 5}
            centerMode={sliderContent.length > 5}
            swipeToSlide={true}
            focusOnSelect={true}
            className={"center"}
            beforeChange={(oldIndex, newIndex) => setCurrentSliderIndex(newIndex)}
            prevArrow={null}
            nextArrow={null}
            accessibility={false}
            responsive={[
              {
                breakpoint: 1350,
                settings: {
                  slidesToShow: 4,
                  infinite: sliderContent.length > 4,
                  centerMode: sliderContent.length > 4,
                },
              },
              {
                breakpoint: 991,
                settings: {
                  slidesToShow: 3,
                  infinite: sliderContent.length > 3,
                  centerMode: sliderContent.length > 3,
                },
              },
              {
                breakpoint: 766,
                settings: {
                  slidesToShow: 2,
                  infinite: sliderContent.length > 2,
                  centerMode: sliderContent.length > 2,
                },
              },
              {
                breakpoint: 375,
                settings: {
                  slidesToShow: 1,
                  infinite: sliderContent.length > 1,
                  centerMode: sliderContent.length > 1,
                },
              },
            ]}
          >
            {sliderContent.map((item, index) => (
              <div key={`SliderSub${index}`}>
                <div className="aspect-[720/483] w-full relative overflow-hidden bg-black flex justify-center items-center">
                  {renderMedia(
                    item.image,
                    "",
                    "max-w-full max-h-full object-contain",
                    {},
                    false,
                    false,
                    null,
                    false
                  )}
                </div>
                <h2 className="text-[#3D3D3D] font-semibold text-2xl table:text-xl mobile:text-lg break-words p-1">
                  {item.title ? item.title : ""}
                </h2>
              </div>
            ))}
          </Slider>
        </div>
      )}
    </div>
  );

  return (
    <>
      <Navbar path={10} />
      <main>
        <section className="h-[250px] page-title flex justify-center items-center">
          <h2 className="text-[#3D3D3D] font-semibold text-5xl table:text-4xl mobile:text-3xl ">
            {lessonContent.name}
          </h2>
        </section>
        <section className="py-10 table:py-6 mobile:py-4">
          <div className="max-w-[1300px] table:px-6 px-10 mobile:px-4 mx-auto table:max-w-[1000px] mb-16 mobile:mb-8">
            <div className="w-full relative table:w-full mobile:w-full flex justify-center items-center" style={{ height: 'calc(100vh - 200px)' }}>
              <img className="w-full h-full object-contain" src={lessonContent.image} />
            </div>
          </div>
          <div className="max-w-[1300px] mx-auto">{renderAlbumContent}</div>
        </section>
      </main>
      <Footer />
      
      {/* Lightbox Modal */}
      {lightboxOpen && sliderContent.length > 0 && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-90 z-[9999] flex justify-center items-center"
          onClick={closeLightbox}
        >
          <div ref={lightboxContainerRef} className="relative w-full h-full flex justify-center items-center p-4">
            {isVideoFile(sliderContent[currentImageIndex]?.image) || isYouTubeUrl(sliderContent[currentImageIndex]?.image) ? (
              renderMedia(
                sliderContent[currentImageIndex]?.image,
                sliderContent[currentImageIndex]?.title || "",
                "max-w-full max-h-full object-contain",
                { onClick: (e) => e.stopPropagation(), controls: true, playsInline: true },
                true,
                true,
                null,
                false
              )
            ) : (
              <img
                src={sliderContent[currentImageIndex]?.image}
                alt={sliderContent[currentImageIndex]?.title || ""}
                className="max-w-full max-h-full object-contain"
                onClick={(e) => e.stopPropagation()}
              />
            )}
            
            {/* Close button */}
            <button
              onClick={closeLightbox}
              className="absolute top-4 right-4 text-white text-4xl hover:text-gray-300 z-10"
              aria-label="關閉"
            >
              ×
            </button>
            
            {/* Navigation arrows */}
            {sliderContent.length > 1 && (
              <>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    prevImage();
                  }}
                  className="absolute left-4 top-1/2 transform -translate-y-1/2 text-white text-8xl hover:text-gray-300 z-10 font-bold"
                  aria-label="上一張"
                >
                  ‹
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    nextImage();
                  }}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 text-white text-8xl hover:text-gray-300 z-10 font-bold"
                  aria-label="下一張"
                >
                  ›
                </button>
              </>
            )}
            
            {/* Image counter */}
            <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 text-white text-lg">
              {currentImageIndex + 1} / {sliderContent.length}
            </div>
            
            {/* Image title */}
            {sliderContent[currentImageIndex]?.title && (
              <div className="absolute bottom-12 left-1/2 transform -translate-x-1/2 text-white text-xl text-center">
                {sliderContent[currentImageIndex].title}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}

export default BuddhismLessonComponent;
